import { z } from 'zod';
import { environmentalBasisSchema } from './environmental-dashboard.js';

/**
 * SIL-473 — Circularity tab (customer Environmental dashboard). Wire DTOs
 * for `GET /api/client/environmental/circularity` — see
 * apps/api/src/routes/client/environmental.ts (route) and apps/customer's
 * `hooks/useEnvironmentalCircularity.ts` (consumer).
 *
 * Per resources/plans/sil-461-dashboard-data-readiness.md's demo verdicts
 * (SIL-471 updates the resale leg's verdict — see below):
 *   - Lifecycle-flows Sankey: HYBRID. The `repurpose` and `disposal` legs
 *     below are REAL — computed from `asset.lifecycle_state_id` (active |
 *     idle | decommissioned bucket into `repurpose`; `disposed` is its own
 *     leg) joined to `v_asset_carbon_footprint_latest`. The THIRD leg,
 *     resale, is REAL for an org with at least one COMPLETED
 *     `marketplace_transaction` row (SIL-471) — `sankey.resale` is `null`
 *     for an org with zero completed sales, and the customer app's
 *     `lib/circularitySampleData.ts` is the fallback fixture that leg
 *     renders in that case, exactly like the Overview tab's AVOIDED/YoY
 *     sample widgets. Never a zeroed object standing in for "no data" — see
 *     `circularityResaleLegSchema`'s own doc comment.
 *   - Disposition comparison: REAL-derived. Computed from a representative
 *     (median-embodied) gpu-class SKU actually present in the org's fleet,
 *     the SIL-420 kernel adder formulas, and this org's
 *     `customer_org_sustainability_config` scalars (falling back to
 *     `SUSTAINABILITY_CONFIG_DEFAULTS` when the org has never customized
 *     them) — tagged 'platform_default' throughout because the config
 *     scalars driving every number here ARE platform defaults unless an
 *     org has overridden them.
 *
 * Every number on this tab is ADVISORY (SIL-459 register): never summed,
 * netted, or axis-shared with an official footprint figure. The UI wraps
 * the whole tab in one quarantine panel — see
 * `components/environmental/circularity/AdvisoryQuarantinePanel.tsx`.
 */

// ---------------------------------------------------------------------------
// Sankey — fleet -> resale (sample, client-only) / repurpose / disposal
// ---------------------------------------------------------------------------

/** One REAL Sankey leg — flow width is the EMBODIED carbon actually carried
 * by the assets on that path this period (never the smaller disposal-adder
 * figure — see `circularityDisposalLegSchema.cat5BurdenKgco2e`, a distinct
 * quantity used only for the disposal leg's terminal annotation). */
export const circularityLegSchema = z.object({
  assetCount: z.number().int().nonnegative(),
  embodiedKgco2e: z.number(),
  basis: environmentalBasisSchema,
});
export type CircularityLeg = z.infer<typeof circularityLegSchema>;

/** The disposal leg additionally carries the Cat-5 disposal-processing
 * burden (`unit_weight_kg / 1000 * disposal_weee_factor`, summed over every
 * disposed asset's class weight) — the terminal annotation figure, distinct
 * from `embodiedKgco2e` (the manufacturing carbon those same assets already
 * carry, which sizes the flow itself). */
export const circularityDisposalLegSchema = circularityLegSchema.extend({
  cat5BurdenKgco2e: z.number(),
});
export type CircularityDisposalLeg = z.infer<typeof circularityDisposalLegSchema>;

/**
 * SIL-471 — resale leg, REAL when the org has completed sales. Flow width
 * (`assetCount`/`embodiedKgco2e`) is embodied-tCO2e-weighted off the sold
 * assets' OWN measured/computed footprint (`v_asset_carbon_footprint_latest`)
 * — the same "flow width = embodied carbon actually carried by the assets on
 * that path" convention the repurpose/disposal legs use, never the smaller
 * SKU-grain figure that drives `avoidedVsNewKgco2e` below.
 * `avoidedVsNewKgco2e` is the terminal annotation — the SAME per-transaction
 * `advisoryAvoidedVsNewKgCo2e` total the Overview tab's AVOIDED KPI sums
 * (SIL-420 kernel, SKU-grain embodied minus the used-purchase adder), so the
 * two surfaces never disagree about the org's resale-avoided figure.
 */
export const circularityResaleLegSchema = circularityLegSchema.extend({
  avoidedVsNewKgco2e: z.number(),
});
export type CircularityResaleLeg = z.infer<typeof circularityResaleLegSchema>;

export const circularitySankeySchema = z.object({
  repurpose: circularityLegSchema,
  disposal: circularityDisposalLegSchema,
  /** `null` (never a zeroed leg) when the org has zero COMPLETED
   * `marketplace_transaction` rows — the UI's `useLifecycleSankey` hook
   * falls back to `lib/circularitySampleData.ts`'s sample fixture in that
   * case, distinguishing "no data" from "zero" per this route's contract. */
  resale: circularityResaleLegSchema.nullable(),
});
export type CircularitySankey = z.infer<typeof circularitySankeySchema>;

// ---------------------------------------------------------------------------
// Disposition comparison — Resale | Repurpose | Disposal for one
// representative (median-embodied) gpu cohort actually owned by the org.
// ---------------------------------------------------------------------------

export const circularityCohortSchema = z.object({
  skuId: z.string(),
  displayName: z.string(),
  manufacturerName: z.string(),
  assetClassId: z.string(),
  embodiedKgco2e: z.number(),
  /** Null only for an asset class with no physical disposal (cloud_vm) —
   * never true for the gpu cohort this route selects, kept nullable to
   * mirror `asset_class_unit_weight`'s own nullability rather than assume. */
  unitWeightKg: z.number().nullable(),
});
export type CircularityCohort = z.infer<typeof circularityCohortSchema>;

export const circularityResaleColumnSchema = z.object({
  /** New-SKU embodied carbon minus the used-purchase adder (SIL-420) — the
   * SAME formula and baseline as the SIL-426 §10 "avoided vs new" advisory
   * number, applied here to the representative cohort. */
  avoidedVsNewKgco2e: z.number(),
  basis: environmentalBasisSchema,
});

export const circularityRepurposeColumnSchema = z.object({
  /** New-embodied avoided (buying a new equivalent unit) minus the
   * operational delta of continuing to run the existing unit instead. */
  avoidedKgco2e: z.number(),
  /** This route does not model an operational-efficiency gap between the
   * redeployed unit and a hypothetical new equivalent (no comparative
   * power-draw data exists for "new" vs "this unit" at the same SKU) — held
   * at 0 and disclosed as such rather than fabricated. See the route's
   * module comment. */
  operationalDeltaKgco2e: z.number(),
  basis: environmentalBasisSchema,
  componentHarvest: z.object({
    /** Avoided new-component embodied carbon from harvesting reusable
     * components (memory/storage/fans — never the chassis/board) off a
     * decommissioned unit instead of buying new. `CIRCULARITY_COMPONENT
     * _HARVEST_FRACTION` below is a SIL-473 demo-scoped platform
     * convention, NOT one of the researched SIL-420 defaults — disclosed
     * as such via its own basis tag. */
    newComponentEmbodiedKgco2e: z.number(),
    basis: environmentalBasisSchema,
  }),
});

export const circularityDisposalColumnSchema = z.object({
  /** The un-numbered floor — Cat-5 disposal burden only. NEVER an avoided
   * figure (SIL-459: no recycling virgin-displacement number). */
  cat5BurdenKgco2e: z.number(),
  basis: environmentalBasisSchema,
});

export const circularityComparisonSchema = z.object({
  /** Null when the org's fleet has no gpu-class asset with a resolved SKU
   * embodied figure yet — the UI renders an honest empty state rather than
   * a fabricated cohort. */
  cohort: circularityCohortSchema.nullable(),
  resale: circularityResaleColumnSchema,
  repurpose: circularityRepurposeColumnSchema,
  disposal: circularityDisposalColumnSchema,
});
export type CircularityComparison = z.infer<typeof circularityComparisonSchema>;

/** SIL-473 demo-scoped platform convention (NOT a SIL-420 researched
 * default): the share of a SKU's embodied carbon attributed to readily
 * harvestable components (memory, storage, fans) versus the
 * chassis/board, which is not re-harvested. Disclosed via the
 * component-harvest sub-row's own 'platform_default' basis tag; never
 * presented as a GHG-Protocol-sourced figure. */
export const CIRCULARITY_COMPONENT_HARVEST_FRACTION = 0.15;

export const environmentalCircularityResponseSchema = z.object({
  sankey: circularitySankeySchema,
  comparison: circularityComparisonSchema,
});
export type EnvironmentalCircularityResponse = z.infer<typeof environmentalCircularityResponseSchema>;
